import { Notification, NotificationType, NotificationChannel } from '../types';

// Mock notification data
let notifications: Notification[] = [
  {
    id: 'notif-1',
    userId: '2',
    type: 'Approval Required',
    title: 'Fund Request Awaiting Approval',
    message: 'Fund request FR-2024-002 requires your approval',
    data: { requestId: 'FR-2024-002', amount: 5800000 },
    read: false,
    createdAt: new Date('2024-01-14T10:30:00Z'),
    channels: ['System', 'Email']
  },
  {
    id: 'notif-2',
    userId: '3',
    type: 'Disbursement',
    title: 'Payment Processed',
    message: 'Payment of TZS 2,500,000 has been processed for FR-2024-001',
    data: { requestId: 'FR-2024-001', amount: 2500000 },
    read: true,
    createdAt: new Date('2024-01-15T14:20:00Z'),
    readAt: new Date('2024-01-15T15:00:00Z'),
    channels: ['System', 'Email']
  }
];

export class NotificationService {
  // Create a new notification
  static createNotification(
    userId: string,
    type: NotificationType,
    title: string,
    message: string,
    data?: Record<string, any>,
    channels: NotificationChannel[] = ['System']
  ): Notification {
    const notification: Notification = {
      id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      userId,
      type,
      title,
      message,
      data,
      read: false,
      createdAt: new Date(),
      channels
    };

    notifications.push(notification);
    
    // In a real application, this would be sent to the backend
    console.log('Notification created:', notification);
    
    return notification;
  }

  // Get notifications for a specific user
  static getUserNotifications(userId: string): Notification[] {
    return notifications.filter(notification => notification.userId === userId);
  }

  // Mark notification as read
  static async markAsRead(notificationId: string): Promise<void> {
    const notification = notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
      notification.readAt = new Date();
    }
  }

  // Get unread count for a user
  static getUnreadCount(userId: string): number {
    return notifications.filter(n => n.userId === userId && !n.read).length;
  }

  // Automated notifications for workflow events
  static notifyProjectCreated(projectId: string, projectName: string): void {
    // Notify HR (Admin) about new project for staff assignment
    this.createNotification(
      '8', // HR Admin user ID
      'Assignment',
      'New Project Created - Staff Assignment Required',
      `New project "${projectName}" has been created and requires staff assignment. Please assign Project Manager, Supervisor, and Cashier.`,
      { 
        projectId, 
        projectName, 
        actionUrl: '/hr/projects' 
      },
      ['System', 'Email']
    );
  }

  static notifyFundRequestSubmitted(requestId: string, projectName: string, amount: number, requesterRole: string): void {
    // Notify Project Manager about new fund request
    this.createNotification(
      '2', // Project Manager user ID
      'Approval Required',
      'New Fund Request Submitted',
      `A new fund request of TZS ${amount.toLocaleString()} has been submitted for project "${projectName}" by ${requesterRole}.`,
      { 
        requestId, 
        projectName, 
        amount,
        actionUrl: '/manager/fund-requests' 
      },
      ['System', 'Email']
    );
  }

  static notifyFundRequestApproved(requestId: string, projectName: string, amount: number, approverRole: string): void {
    // Notify next approver in chain
    if (approverRole === 'Project Manager') {
      // Notify MD/TD
      this.createNotification(
        '1', // MD user ID
        'Approval Required',
        'Fund Request Approved by Project Manager',
        `Fund request ${requestId} for project "${projectName}" has been approved by Project Manager and requires your approval.`,
        { 
          requestId, 
          projectName, 
          amount,
          actionUrl: '/funds' 
        },
        ['System', 'Email']
      );
    } else if (approverRole === 'MD' || approverRole === 'TD') {
      // Notify Accountant for payment
      this.createNotification(
        '3', // Accountant user ID
        'Disbursement',
        'Fund Request Approved - Payment Required',
        `Fund request ${requestId} for project "${projectName}" has been approved by ${approverRole}. Please process payment of TZS ${amount.toLocaleString()}.`,
        { 
          requestId, 
          projectName, 
          amount,
          actionUrl: '/accounting' 
        },
        ['System', 'Email']
      );
    }
  }

  static notifyOfficeBudgetRequestSubmitted(requestId: string, purpose: string, amount: number): void {
    // Notify HR (Admin) about new office budget request
    this.createNotification(
      '8', // HR Admin user ID
      'Approval Required',
      'New Office Budget Request',
      `A new office budget request of TZS ${amount.toLocaleString()} has been submitted for "${purpose}".`,
      { 
        requestId, 
        purpose, 
        amount,
        actionUrl: '/office/budget-approval' 
      },
      ['System', 'Email']
    );
  }

  static notifyOfficeBudgetRequestApproved(requestId: string, purpose: string, amount: number, approverRole: string): void {
    // Notify next approver in chain
    if (approverRole === 'HR (Admin)') {
      // Notify MD/TD
      this.createNotification(
        '1', // MD user ID
        'Approval Required',
        'Office Budget Request Approved by HR',
        `Office budget request ${requestId} for "${purpose}" has been approved by HR and requires your approval.`,
        { 
          requestId, 
          purpose, 
          amount,
          actionUrl: '/office/budget-approval' 
        },
        ['System', 'Email']
      );
    } else if (approverRole === 'MD' || approverRole === 'TD') {
      // Notify Accountant for payment
      this.createNotification(
        '3', // Accountant user ID
        'Disbursement',
        'Office Budget Request Approved - Payment Required',
        `Office budget request ${requestId} for "${purpose}" has been approved by ${approverRole}. Please process payment of TZS ${amount.toLocaleString()}.`,
        { 
          requestId, 
          purpose, 
          amount,
          actionUrl: '/accountant/office' 
        },
        ['System', 'Email']
      );
    }
  }

  static notifyExpenseRecorded(expenseId: string, category: string, amount: number, receiptType: string): void {
    // If Invoice receipt type, notify MD/TD for approval
    if (receiptType === 'Invoice') {
      this.createNotification(
        '1', // MD user ID
        'Approval Required',
        'Expense Approval Needed',
        `A new expense of TZS ${amount.toLocaleString()} has been recorded by the cashier with Invoice receipt type. Please review and approve or reject this expense.`,
        { 
          expenseId, 
          category, 
          amount,
          actionUrl: '/admin/expense-approval' 
        },
        ['System', 'Email']
      );

      this.createNotification(
        '7', // TD user ID
        'Approval Required',
        'Expense Approval Needed',
        `A new expense of TZS ${amount.toLocaleString()} has been recorded by the cashier with Invoice receipt type. Please review and approve or reject this expense.`,
        { 
          expenseId, 
          category, 
          amount,
          actionUrl: '/admin/expense-approval' 
        },
        ['System', 'Email']
      );
    }
  }

  static notifyPaymentProcessed(requestId: string, amount: number, projectName?: string): void {
    // Notify requester about payment
    this.createNotification(
      '6', // Store Keeper user ID (for office budget) or Project Manager (for fund requests)
      'Disbursement',
      'Payment Processed',
      `Payment of TZS ${amount.toLocaleString()} has been processed${projectName ? ` for project "${projectName}"` : ''}.`,
      { 
        requestId, 
        amount,
        projectName,
        actionUrl: projectName ? '/manager/fund-requests' : '/storekeeper/office/expenses' 
      },
      ['System', 'Email']
    );
  }

  // Clear all notifications (for testing)
  static clearAllNotifications(): void {
    notifications = [];
  }

  // Get all notifications (for admin purposes)
  static getAllNotifications(): Notification[] {
    return notifications;
  }
}

export default NotificationService; 